import { createPathWithAllFilters } from "@modules/azm/api"
import { fetchApiWithNewLocation } from "@modules/azm/filter-selection/location-input"
import { setShowResultsButtonLoading } from "@modules/azm/filter-selection/set-show-results-button"
import { setAZMFilterPathLocationToSessionStorage } from "@modules/global/filter-path/filter-path-handler.ts"
import { setFilterLocation } from "@modules/location/location"
import { possibleRedirectToSpecialLocation } from "@modules/location/special-locations.ts"
import type { FilterLocation } from "@modules/types"
import { Cookie } from "@modules/util/Cookie"
import { removeQueryParameter } from "@modules/util/query-parameter"
import { isMobile } from "../util/device"
import { getRequestUri } from "../util/request-uri"
import { Autocomplete, type AutocompleteConfig, type AutocompleteItem } from "./Autocomplete"

export class AutocompleteLds extends Autocomplete {
    filterDataCookie: Cookie
    locationAjaxRequest: any
    actualLocationName = ""
    regioSearchInput: typeof $
    selectedLocation: FilterLocation | undefined

    constructor(config: AutocompleteConfig = {}) {
        // Call parent constructor
        super(config)
        this.regioSearchInput = $("#regiosuche")

        this.filterDataCookie = new Cookie("clearFilterData", {
            path: "/",
            domain: SiteInfo.cookieDomain,
        })
    }

    sourceLazy = (request: any, response: any) => {
        if (!request.term) {
            // Leerstring als Request-Term bedeutet, dass der aktuell
            //  eingegebene Begriff nicht verändert wurde, daher ohne
            //  Vorschlagsliste herausspringen
            return response([])
        }

        this.source(request, response)
    }

    source = (request: any, response: any) => {
        this.cancelRequest()

        if (SiteInfo.locationSearchApiUrl.includes("/api/location-search/list")) {
            return this.locationSearchList(request, response)
        }

        this.locationAjaxRequest = $.ajax({
            url: SiteInfo.locationSearchApiUrl,
            data: {
                searchString: request.term,
                searchAddAlleRegionen: this.showAlleRegionenList,
            },
            dataType: "json",
            xhrFields: {
                withCredentials: true,
            },
            error: () => {
                response([])
            },
            success: (data: []) => {
                const locationForm = $(".regio-form")

                this.currentSearchText = request.term

                if (data.length === 0) {
                    $(this.config.elementsInput).removeClass("autocomplete-open")
                }

                siteConfig.locationSuggestions = data

                // wenn nach PLZ gesucht wurde, die Source ist ein Objekt
                this.initSavedSuggestions()

                if (data.length > 0) {
                    locationForm.remove("submit-disabled")
                } else {
                    locationForm.addClass("submit-disabled")
                }

                return response(siteConfig.locationSuggestions)
            },
        })
    }

    open = (event: Event) => {
        $("#regiosuche").trigger("change.value:Autocomplete", {
            instance: this,
            item: this.focusedItem,
            event: event,
            value: $("#regiosuche").val(),
            autoFocus: true,
        })
    }

    change = (event: Event) => {
        $("#regiosuche").trigger("close.value:Autocomplete", {
            instance: this,
            event: event,
            item: this.focusedItem,
        })
    }

    select(event: Event, ui: any) {
        const regioSearchElement = $("#regiosuche")
        const searchText = regioSearchElement.val()
        const fieldItem = ui.item
        const locationForm = $(".regio-form")
        let exactMatchFound = false

        if (null !== this.focusedItem) {
            this.regioSearchInput
                .val(fieldItem.value)
                .data("regioSelected", fieldItem.value || "")
                .data("regioSlug", fieldItem.slug.replace(" ", "-").toLowerCase() || "")
                .data("regioPostal", fieldItem.plz || "")
                .data("regioLand", fieldItem.land || "")
                .data("regioType", fieldItem.Type || "")

            locationForm.removeClass("submit-disabled")

            $(".ui-autocomplete").hide()
            regioSearchElement.removeClass("autocomplete-open")

            regioSearchElement.trigger("selected.item:Autocomplete", {
                instance: this,
                event: event,
                item: fieldItem,
            })

            this.selectedLocation = {
                slug: this.focusedItem.Slug,
                name: this.focusedItem.Name,
                cityId: this.focusedItem.LocationId,
                postalCodes: [],
                locationType: this.focusedItem.Type.replace("top-ort", "ort"),
                plz: this.focusedItem.plz || "",
                address: "",
                addressName: "",
            }

            // on the index page there is no location modal which shows the result count and has a "show" button. It needs to replace the results directly
            if (isMobile() && !SiteInfo.isIndex) {
                this.regioSearchInput.addClass("ui-autocomplete-loading")
                setShowResultsButtonLoading()
                fetchApiWithNewLocation(this.selectedLocation).then(() => {
                    this.regioSearchInput.removeClass("ui-autocomplete-loading")
                })
            } else {
                const slug = this.selectedLocation.plz
                    ? `${this.selectedLocation.plz}-${this.selectedLocation.slug}`
                    : this.selectedLocation.slug
                if (possibleRedirectToSpecialLocation(window.location.href, slug)) {
                    return
                }
                removeQueryParameter("page")
                removeQueryParameter("umkreis")
                removeQueryParameter("adresse")
                setFilterLocation({
                    ...this.selectedLocation,
                    slug,
                })

                setAZMFilterPathLocationToSessionStorage(createPathWithAllFilters(SiteInfo.controller))
                window.location.href = createPathWithAllFilters(SiteInfo.controller)
            }
        } else {
            this.enterPressed = true

            exactMatchFound = this._redirectIfExactMatch(
                searchText,
                fieldItem.Name,
                fieldItem.Slug,
                fieldItem.Plz,
                fieldItem.Type,
                fieldItem.Land || null,
            )

            if (true === exactMatchFound) {
                event.preventDefault()

                $(this.config.elementsInput).data("regioSlug", fieldItem.Slug)
                $(".ui-autocomplete").hide()

                return false
            }
            $(this.config.elementsInput).val(searchText)
            $(this.config.elementsInput).autocomplete("search", searchText)
            event.preventDefault()
        }
    }

    cancelRequest() {
        if (this.locationAjaxRequest && "abort" in this.locationAjaxRequest) {
            this.locationAjaxRequest.abort()
            this.locationAjaxRequest = null
        }
    }

    _redirectIfExactMatch(searchText: string, name: string, slug: string, plz: string, type: string, land = "") {
        if (slug && possibleRedirectToSpecialLocation(window.location.href, slug)) {
            return true
        }

        let redirectUrl = ""
        let filterPath = ""
        let pageSlug = SiteInfo.redirectToPageSlug

        if ("index" === SiteInfo.controller) {
            if (slug in SiteInfo.redirectToPageSlugSpecial) {
                pageSlug = SiteInfo.redirectToPageSlugSpecial[slug]
            } else if (type in SiteInfo.redirectToPageSlugSpecial) {
                pageSlug = SiteInfo.redirectToPageSlugSpecial[type]
            }
        }

        if ("sex-anzeigen" === pageSlug) {
            filterPath = this.buildFilterPart()

            if (filterPath.substring(0, 1) !== "/") {
                filterPath = `/${filterPath}`
            }
        }

        if (plz && searchText === plz) {
            this.enterPressed = false

            if (SiteInfo.controller === "index") {
                this.filterDataCookie.expires = Cookie.getExpireDate(3600 * 24)
                this.filterDataCookie.value = "1"
            }

            if (pageSlug && pageSlug !== "home") {
                redirectUrl = `/${plz}-${slug}/${pageSlug}${filterPath}`
                this._updateRegioUrl(redirectUrl, {
                    plz: plz,
                    slug: slug,
                    type: type,
                    land: land,
                })
            } else {
                redirectUrl = `/${plz}-${slug}${filterPath}`
                this._updateRegioUrl(redirectUrl, {
                    plz: plz,
                    slug: slug,
                    type: type,
                    land: land,
                })
            }

            return true
        }
        if (searchText === name.toLowerCase().trim()) {
            this.enterPressed = false

            if (SiteInfo.controller === "index") {
                this.filterDataCookie.expires = Cookie.getExpireDate(3600 * 24)
                this.filterDataCookie.value = "1"
            }

            if (pageSlug && pageSlug !== "home") {
                redirectUrl = `/${slug}/${pageSlug}${filterPath}`
                this._updateRegioUrl(redirectUrl, {
                    plz: "",
                    slug: slug,
                    type: type,
                    land: land,
                })
            } else {
                redirectUrl = `/${slug}${filterPath}`
                this._updateRegioUrl(redirectUrl, {
                    plz: "",
                    slug: slug,
                    type: type,
                    land: land,
                })
            }

            return true
        }
        return false
    }

    _renderMenu(ul: typeof $, items: any) {
        let currentCategory = ""
        const searchText = $(this.config.elementsInput).val().toLowerCase()
        let exactMatchFound = false

        $(this.config.elementsInput).addClass("autocomplete-open")

        if (true === this.enterPressed) {
            $.each(items, (_: number, item: AutocompleteItem) => {
                exactMatchFound = this._redirectIfExactMatch(searchText, item.Name, item.Slug, item.Plz, item.Type)

                if (true === exactMatchFound) {
                    $(this.config.elementsInput).data("regioSlug", item.Slug)

                    return false // break each-Schleife
                }
            })
        }

        if (exactMatchFound) {
            $(".ui-autocomplete").removeClass("active")
            return false
        }

        $.each(items, (index: number, item: AutocompleteItem) => {
            let li: typeof $

            items[index].label = item.Name
            items[index].value = item.Name
            items[index].slug = item.Slug
            items[index].plz = item.Plz
            items[index].land = item.Land
            items[index].searchTerm = this.currentSearchText

            if (item.Category !== currentCategory) {
                ul.append(`<li class='ui-autocomplete-category'>${item.Category}</li>`)
                currentCategory = item.Category
            }
            li = this._renderItem(ul, item)

            if (item.Category) {
                li.attr("aria-label", `${item.Category} : ${item.Name}`)
            }
        })

        $(".ui-autocomplete").addClass("active")
    }

    _renderItem(ul: typeof $, item: any) {
        const link = $(`<a>${item.Name}</a>`)
        const flag = $(`<span class="location-flag ${item.Land}"></span>`)

        link.prepend(flag)

        return $("<li></li>").data("item.autocomplete", item).append(link).appendTo(ul)
    }

    _inputDataChanged(keyEvent: KeyboardEvent) {
        const locationForm = $(".regio-form")
        let disableSubmission = true
        const regioSearchElement = $(this.config.elementsInput)
        const searchText = regioSearchElement.val()

        // @ts-expect-error idk
        if (/^[a-z0-9 \-äöüß]$/i.test(keyEvent.originalEvent.key)) {
            this.focusedItem = null
        }

        this.actualInputValue = searchText.toLowerCase()
        $("#regiosuche").trigger("change.value:Autocomplete", {
            instance: this,
            item: this.focusedItem,
            event: keyEvent,
            value: searchText,
            autoFocus: false,
        })

        $(keyEvent.currentTarget).autocomplete({
            source: this.source.bind(this),
            minLength: 2,
            autoFocus: true,
            value: regioSearchElement.val(),
        })

        if (regioSearchElement.val() === "") {
            regioSearchElement.data("regioSlug", regioSearchElement.val())

            if (isMobile()) {
                $(document).trigger("updated.regio:Autocomplete", [
                    { plz: "", slug: "", type: "overall", cleared: true },
                ])
            }
        }

        $(keyEvent.currentTarget).removeClass("current-location")
        $("#regio-vorschlaege").removeClass("keep-open")

        locationForm.removeClass("submit-disabled")

        if ("" !== regioSearchElement.val() && this.actualLocationName !== regioSearchElement.val()) {
            $.each(siteConfig.locationSuggestions, (_: number, option: any) => {
                if (searchText.toLowerCase() === option.Name.toLowerCase()) {
                    disableSubmission = false
                    return false
                }
            })

            if (disableSubmission) {
                locationForm.addClass("submit-disabled")
            }
        }
    }

    _formSubmitted(keyEvent: KeyboardEvent) {
        const formElement = $(keyEvent.currentTarget)

        keyEvent.stopPropagation()
        keyEvent.preventDefault()

        if (formElement.hasClass("submit-disabled")) {
            return
        }

        const slug = $(this.config.elementsInput).data("regioSlug") || ""
        const postalCode = $(this.config.elementsInput).data("regioPostal") || ""
        const locationType = $(this.config.elementsInput).data("regioType") || ""
        const locationLand = $(this.config.elementsInput).data("regioLand") || null
        let urlComplete = ""
        let pageSlug = SiteInfo.redirectToPageSlug
        let filterPath = ""

        if ("index" === SiteInfo.controller) {
            if (slug in SiteInfo.redirectToPageSlugSpecial) {
                pageSlug = SiteInfo.redirectToPageSlugSpecial[slug]
            } else if (locationType in SiteInfo.redirectToPageSlugSpecial) {
                pageSlug = SiteInfo.redirectToPageSlugSpecial[locationType]
            }
        }

        $(this.config.elementsInput).removeClass("autocomplete-open")
        $(".ui-autocomplete").hide()

        if (this.actualInputValue === "" && SearchConfig.location.name !== "") {
            urlComplete = `/${pageSlug}`

            window.setTimeout(() => {
                $("#regiosuche").val("")
            }, 1)

            if (SiteInfo.controller === "index") {
                this.filterDataCookie.expires = Cookie.getExpireDate(3600 * 24)
                this.filterDataCookie.value = "1"
            }

            if ("sex-anzeigen" === pageSlug) {
                filterPath = this.buildFilterPart()
                if (filterPath.substring(0, 1) !== "/") {
                    filterPath = `/${filterPath}`
                }

                urlComplete += filterPath
                urlComplete = this.clearUrl(urlComplete)
            }
            this._updateRegioUrl(urlComplete, {
                plz: postalCode,
                slug: slug,
                type: locationType,
                land: null,
            })
            return
        }

        if ("" !== slug && "" !== $(this.config.elementsInput).val()) {
            urlComplete = "/"

            if ("" !== postalCode) {
                urlComplete += `${postalCode}-`
            }

            urlComplete += slug
        }

        if ("home" !== pageSlug || "" === slug) {
            urlComplete += `/${pageSlug}`
        }

        if (SiteInfo.controller === "index") {
            this.filterDataCookie.expires = Cookie.getExpireDate(3600 * 24)
            this.filterDataCookie.value = "1"
        }

        if ("sex-anzeigen" === pageSlug) {
            filterPath = this.buildFilterPart()
            if (filterPath.substring(0, 1) !== "/") {
                filterPath = `/${filterPath}`
            }

            urlComplete += filterPath
            urlComplete = this.clearUrl(urlComplete)
        }

        if (possibleRedirectToSpecialLocation(window.location.href, slug)) {
            return
        }
        this._updateRegioUrl(urlComplete, {
            plz: postalCode,
            slug: slug,
            type: locationType,
            land: locationLand,
        })
    }

    clearUrl(urlComplete: string) {
        const queryString = urlComplete.split("?")
        let elements = []
        let newUrlComplete = urlComplete
        const newQueryStringArr = []

        if (queryString[1] && queryString[1] !== "") {
            newUrlComplete = queryString[0]

            elements = queryString[1].split("&")
            for (const i in elements) {
                const getElementArr = elements[i].split("=")
                const getKey = getElementArr[0]
                let getVal = getElementArr[1]

                if ("suchtext" === getKey && SearchConfig && SearchConfig.filteredSearchText != null) {
                    if (SearchConfig.filteredSearchText !== "") {
                        getVal = decodeURIComponent(SearchConfig.filteredSearchText)
                        SearchConfig.filteredSearchText = null
                        newQueryStringArr.push(`${getKey}=${getVal}`)
                    } else {
                    }
                } else if (["umkreis", "page", "strasse", "hausnr", "adresse"].indexOf(getKey) < 0) {
                    newQueryStringArr.push(`${getKey}=${getVal}`)
                }
            }
        }

        if (newQueryStringArr.length > 0) {
            newUrlComplete = `${newUrlComplete}?${newQueryStringArr.join("&")}`
        }

        return newUrlComplete
    }

    buildFilterPart() {
        const replaceSpecial = new RegExp(
            `(${Object.keys(SiteInfo.redirectToPageSlugSpecial)
                .map((index) => SiteInfo.redirectToPageSlugSpecial[index])
                .join("|")})`,
        )

        const locationPlz = "" !== siteConfig.locationPlz ? `${siteConfig.locationPlz}-` : ""

        return getRequestUri()
            .replace(SiteInfo.url, "")
            .replace(SiteInfo.redirectToPageSlug, "")
            .replace(replaceSpecial, "")
            .replace(`/${locationPlz}${SearchConfig.location.slug}`, "")
    }

    _enterPressed(_: KeyboardEvent) {
        $(".ui-autocomplete").removeClass("active")

        this.enterPressed = true
    }

    private locationSearchList(request: any, response: any) {
        this.locationAjaxRequest = $.ajax({
            url: SiteInfo.locationSearchApiUrl,
            data: {
                searchtext: request.term,
                searchAddAlleRegionen: this.showAlleRegionenList,
            },
            dataType: "json",
            xhrFields: {
                withCredentials: true,
            },
            error: () => {
                response([])
            },
            success: (data: { entries: []; messageId: string }) => {
                const locationForm = $(".regio-form")

                this.currentSearchText = request.term

                if (data.entries.length === 0) {
                    $(this.config.elementsInput).removeClass("autocomplete-open")
                }

                siteConfig.locationSuggestions = data.entries

                // wenn nach PLZ gesucht wurde, die Source ist ein Objekt
                this.initSavedSuggestions()

                if (data.entries.length > 0) {
                    locationForm.remove("submit-disabled")
                } else {
                    locationForm.addClass("submit-disabled")
                }

                return response(siteConfig.locationSuggestions)
            },
        })
    }
}
